import React from 'react';
import { Upload, FileCheck, AlertCircle, X, User, LogOut, BarChart3, LayoutList } from 'lucide-react';

export default function UploadStage({ user, error, isDragging, fileInputRef, onDragOver, onDragLeave, onDrop, onInputChange, onClearError, onBackToDashboard, multiple }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
      {/* Auth Header */}
      <header className="px-6 py-3 flex items-center justify-between">
        {onBackToDashboard ? (
          <button
            onClick={onBackToDashboard}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded-lg transition-colors"
          >
            <LayoutList className="w-4 h-4" />
            My POs
          </button>
        ) : <span />}
        {user && (
          <div className="flex items-center gap-4">
            {user.userRoles?.includes('po_processor_admins') && (
              <a
                href="/admin"
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-400 hover:text-amber-400 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <BarChart3 className="w-4 h-4" />
                Admin
              </a>
            )}
            <div className="flex items-center gap-2 text-slate-300">
              <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-slate-400" />
              </div>
              <span className="text-sm">{user.userDetails}</span>
              <span className="text-slate-600">Welcome</span>
            </div>
            <a
              href="/.auth/logout?post_logout_redirect_uri=/"
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </a>
          </div>
        )}
      </header>

      <div className="flex-1 flex items-center justify-center p-6">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-amber-500/20">
              <FileCheck className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white tracking-tight">Order Processor</h1>
          </div>
          <p className="text-slate-400">Upload a purchase order to extract and validate</p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3 text-red-400">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
            <button onClick={onClearError} className="ml-auto">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div
          className={`relative border-2 border-dashed rounded-2xl p-12 transition-all duration-300 cursor-pointer
            ${isDragging
              ? 'border-amber-400 bg-amber-400/10 scale-[1.02]'
              : 'border-slate-600 bg-slate-800/50 hover:border-slate-500 hover:bg-slate-800'
            }`}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple={multiple}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.html,.htm,.xml,.cxml,.json"
            onChange={onInputChange}
          />
          <div className="text-center">
            <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl flex items-center justify-center transition-all duration-300
              ${isDragging ? 'bg-amber-400 scale-110' : 'bg-slate-700'}`}>
              <Upload className={`w-10 h-10 transition-colors ${isDragging ? 'text-slate-900' : 'text-slate-400'}`} />
            </div>
            <p className="text-xl text-white font-medium mb-2">
              {isDragging ? 'Drop your file(s) here' : multiple ? 'Drag & drop your PO file(s)' : 'Drag & drop your PO file'}
            </p>
            <p className="text-slate-500 mb-4">or click to browse{multiple ? ' — select multiple to process them all at once' : ''}</p>
            <div className="flex items-center justify-center gap-2 text-sm text-slate-500">
              <span className="px-2 py-1 bg-slate-700 rounded">PDF</span>
              <span className="px-2 py-1 bg-slate-700 rounded">Word</span>
              <span className="px-2 py-1 bg-slate-700 rounded">Excel</span>
              <span className="px-2 py-1 bg-slate-700 rounded">TXT</span>
              <span className="px-2 py-1 bg-slate-700 rounded">HTML</span>
              <span className="px-2 py-1 bg-slate-700 rounded">XML</span>
            </div>
          </div>
        </div>

        <p className="mt-6 text-center text-sm text-slate-500">
          Powered by AI extraction with automatic validation against A+
        </p>
      </div>
      </div>
    </div>
  );
}
